import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Copy, Upload, Loader2, Building2, CheckCircle2 } from "lucide-react";
import { fmt } from "@/lib/format";
import { formatApiError } from "@/lib/errors";

export default function BankTransfer() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const propertyId = params.get("property_id");
  const brickId = params.get("brick_id");
  const numBricks = parseInt(params.get("num_bricks") || "1");
  const amountParam = parseFloat(params.get("amount") || "0");

  const [bank, setBank] = useState(null);
  const [form, setForm] = useState({
    amount: amountParam || "",
    account_used: "naira",
    depositor_name: "",
    reference_number: "",
    teller_url: "",
    note: "",
  });
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const backend = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    api.get("/bank-details").then(({ data }) => setBank(data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const copy = (v, label) => {
    navigator.clipboard.writeText(v);
    toast.success(`${label} copied`);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Max 8MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/kyc/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("teller_url", `${backend}${data.url}`);
      toast.success("Teller uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.teller_url) {
      toast.error("Upload the deposit teller/receipt first");
      return;
    }
    if (!form.depositor_name) {
      toast.error("Depositor name is required");
      return;
    }
    setBusy(true);
    try {
      const body = {
        amount: parseFloat(form.amount),
        account_used: form.account_used,
        depositor_name: form.depositor_name,
        reference_number: form.reference_number || undefined,
        teller_url: form.teller_url,
        note: form.note || undefined,
      };
      if (propertyId) {
        body.property_id = propertyId;
        body.num_bricks = numBricks;
      } else if (brickId) {
        body.brick_id = brickId;
      }
      const { data } = await api.post("/bank-transfer/submit", body);
      setDone(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Submission failed"));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
        <div className="bp-card rounded-2xl p-10 max-w-md w-full text-center">
          <CheckCircle2 className="w-12 h-12 text-[#D4AF37] mx-auto" />
          <h2 className="font-heading text-3xl text-white mt-4" data-testid="bt-success-title">
            Submission received
          </h2>
          <p className="text-gray-400 text-sm mt-2">
            We'll verify your bank transfer of {fmt(done.amount, { decimals: true })} and assign your bricks. You'll get an email once approved.
          </p>
          <div className="flex gap-3 mt-6">
            <Button className="flex-1 btn-gold rounded-full" onClick={() => navigate("/dashboard")}>
              Dashboard
            </Button>
            <Button variant="outline" className="flex-1 rounded-full" onClick={() => navigate("/transactions")}>
              History
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!bank) return <div className="p-8 text-gray-500">Loading...</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
      <div className="flex items-center gap-3 mb-2">
        <Building2 className="w-5 h-5 text-[#D4AF37]" />
        <div className="overline">Bank transfer</div>
      </div>
      <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-white mb-3">
        Pay by transfer
      </h1>
      <p className="text-gray-400 max-w-2xl mb-8">
        Transfer to our company account, then upload the teller/receipt below. Our admin team verifies within business hours and assigns your bricks.
      </p>

      {/* Bank Details Card */}
      <div className="bp-card rounded-2xl p-6 mb-6">
        <div className="overline mb-4">Our bank details</div>
        <div className="grid md:grid-cols-2 gap-5">
          <BankAccount label="Naira account (NGN)" name={bank.account_name} bank={bank.bank_name} number={bank.naira_account} onCopy={() => copy(bank.naira_account, "Naira account")} />
          <BankAccount label="Dollar account (USD)" name={bank.account_name} bank={bank.bank_name} number={bank.dollar_account} onCopy={() => copy(bank.dollar_account, "Dollar account")} />
        </div>
      </div>

      {/* Submission Form */}
      <form onSubmit={submit} className="bp-card rounded-2xl p-6 space-y-4">
        <div className="overline">Confirm your transfer</div>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Account used">
            <Select value={form.account_used} onValueChange={(v) => set("account_used", v)}>
              <SelectTrigger data-testid="bt-account-used" className="bg-[#050914] border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0B1120] text-white border-white/10">
                <SelectItem value="naira">Naira ({bank.naira_account})</SelectItem>
                <SelectItem value="dollar">Dollar ({bank.dollar_account})</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Amount (NGN)">
            <Input
              data-testid="bt-amount"
              type="number"
              value={form.amount}
              onChange={(e) => set("amount", e.target.value)}
              className="bg-[#050914] border-white/10 focus:border-[#D4AF37]"
            />
          </Field>
          <Field label="Depositor name">
            <Input
              data-testid="bt-depositor"
              value={form.depositor_name}
              onChange={(e) => set("depositor_name", e.target.value)}
              placeholder="Name on the bank account you sent from"
              className="bg-[#050914] border-white/10 focus:border-[#D4AF37]"
            />
          </Field>
          <Field label="Reference number (optional)">
            <Input
              data-testid="bt-reference"
              value={form.reference_number}
              onChange={(e) => set("reference_number", e.target.value)}
              placeholder="Bank transaction reference"
              className="bg-[#050914] border-white/10 focus:border-[#D4AF37]"
            />
          </Field>
        </div>

        <div>
          <Label className="text-gray-300 text-xs uppercase tracking-wider">Note (optional)</Label>
          <Textarea rows={2} value={form.note} onChange={(e) => set("note", e.target.value)} className="mt-1 bg-[#050914] border-white/10" />
        </div>

        <div>
          <Label className="text-gray-300 text-xs uppercase tracking-wider">
            Deposit teller / receipt <span className="text-[#D4AF37]">*</span>
          </Label>
          <div className="mt-2 flex items-center gap-3">
            {form.teller_url ? (
              <img src={form.teller_url} alt="teller" className="w-20 h-20 rounded-lg object-cover border border-white/10" />
            ) : (
              <div className="w-20 h-20 rounded-lg border border-dashed border-white/10 bg-[#050914] flex items-center justify-center">
                <Upload className="w-5 h-5 text-gray-600" />
              </div>
            )}
            <label className={`inline-flex items-center gap-2 px-3 py-2 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] text-xs cursor-pointer hover:bg-[#D4AF37]/10 transition ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              {uploading ? "Uploading..." : form.teller_url ? "Replace" : "Upload teller"}
              <input type="file" accept="image/*,application/pdf" onChange={onFile} className="hidden" data-testid="bt-teller-file" />
            </label>
          </div>
        </div>

        <Button type="submit" disabled={busy} className="btn-gold rounded-full h-12 px-10" data-testid="bt-submit">
          {busy ? "Submitting..." : "Submit for verification"}
        </Button>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-gray-300 text-xs uppercase tracking-wider">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function BankAccount({ label, name, bank, number, onCopy }) {
  return (
    <div className="bp-surface rounded-xl p-4">
      <div className="overline text-[10px]">{label}</div>
      <div className="mt-2 text-sm text-white">{name}</div>
      <div className="text-xs text-gray-400">{bank}</div>
      <div className="mt-3 flex items-center gap-2">
        <div className="font-mono text-lg text-[#D4AF37]">{number}</div>
        <button type="button" onClick={onCopy} className="text-gray-400 hover:text-[#D4AF37]">
          <Copy className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
